<script>
import api from '@/helpers/api'
import { required, minValue, decimal } from 'vuelidate/lib/validators'

export default {
  name: 'product.info',
  props: {
    product_id: { type: String, required: true }
  },
  data: () => ({
    isLoading: false,
    product: {},
    materials: [],
    showProductSelector: true,
    fields: [
      {
        key: 'material_id',
        label: 'Material',
        class: 'text-left align-middle'
      },
      {
        key: 'quantity',
        label: 'Qty.',
        sortDirection: 'desc',
        class: 'text-center align-middle',
        thStyle: { width: '100px' }
      },
      {
        key: 'actions',
        label: '',
        sortDirection: 'desc',
        thStyle: { width: '200px' },
        class: 'text-left align-middle'
      }
    ]
  }),
  mounted () {
    this.onInit()
  },
  watch: {
    product_id: { handler: 'onInit', immediate: true }
  },
  validations: {
    product: {
      cost_price: {
        required,
        minValue: minValue(0),
        decimal
      }
    }
  },
  computed: {
    filteredMaterials () {
      return this.materials.filter(m => !m.marked_for_delete)
    }
  },
  methods: {
    onProductSelected (p) {
      this.materials = [
        ...this.materials,
        {
          product_id: this.product_id,
          product: this.product,
          material: { ...p },
          material_id: p.id,
          quantity: 1,
          marked_as_new: true
        }
      ]
    },
    onRemove (mat) {
      if (mat.marked_as_new)
        this.materials = this.materials.filter(
          m => m.material_id !== mat.material_id
        )
      else mat.marked_for_delete = true
    },
    async onInit () {
      this.isLoading = true
      let item = await api.items.fetch({
        id: this.product_id
      })
      this.product = {
        ...item
      }

      let { items } = await api.productMaterials.fetchPage({
        page: 1,
        pageSize: -1,
        orderBy: [{ created_at: 'asc' }],
        filters: [{ product_id: { _eq: this.product_id } }]
      })
      this.materials = [
        ...items.map(i => ({
          ...i,
          marked_for_delete: false
        }))
      ]
      this.isLoading = false
    },
    async onSave () {
      let valid = true

      if (valid) {
        const data = {
          ...this.product
        }

        try {
          this.isLoading = true
          let item = await api.items.update({
            ...data,
            id: this.product_id
          })

          this.materials
            .filter(m => m.marked_as_new)
            .forEach(async mat => {
              const { product_id, material_id, quantity } = mat
              await api.productMaterials.create({
                product_id,
                material_id,
                quantity
              })
            })

          this.materials
            .filter(m => m.marked_for_delete)
            .forEach(async mat => {
              const { product_id, material_id, quantity } = mat
              await api.productMaterials.delete({
                product_id,
                material_id
              })
            })

          this.materials
            .filter(m => !m.marked_for_delete)
            .forEach(async mat => {
              const { product_id, material_id, quantity } = mat
              await api.productMaterials.update({
                product_id,
                material_id,
                quantity
              })
            })

          this.onInit()

          this.$bvToast.toast(`Product info was saved!`, {
            title: 'Changes Saved',
            solid: true,
            toaster: 'b-toaster-top-center',
            variant: 'success',
            autoHideDelay: 1000,
            appendToast: true
          })
          this.$emit('update')
          this.isLoading = false
        } catch (error) {
          this.isLoading = false

          this.$bvToast.toast(`${error}!`, {
            title: 'Error',
            solid: true,
            toaster: 'b-toaster-top-center',
            variant: 'danger',
            autoHideDelay: 1000,
            appendToast: true
          })
        }
      }
    }
  }
}
</script>
<template>
  <g-overlay :show="isLoading">
    <b-card>
      <h4 class="card-title border-bottom pb-3 mb-4">Purchase Information</h4>

      <b-form-group
        label="Supplier"
        label-for="supplier"
        label-cols-sm="3"
        label-cols-lg="2"
      >
        <app-supplier-select
          v-model="product.supplier_id"
          id="supplier"
          :show-labels="true"
          :allow-empty="true"
        />
      </b-form-group>
      <b-form-group
        label="Supplier Reference"
        label-for="product_supplier_reference"
        label-cols-sm="3"
        label-cols-lg="2"
      >
        <b-form-input
          id="product_supplier_reference"
          v-model="product.supplier_reference"
          type="text"
          autocomplete="product_supplier_reference"
          placeholder="Supplier product reference"
          :style="{ maxWidth: '300px' }"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        label="Cost Price *"
        label-for="cost_price"
        label-cols-lg="2"
        label-cols-sm="3"
      >
        <b-input-group>
          <b-form-input
            id="cost_price"
            v-model.number="$v.product.cost_price.$model"
            required
            :style="{ maxWidth: '100px' }"
            class="text-center"
          ></b-form-input>
          <b-input-group-append is-text> EUR </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <h4 class="card-title border-bottom pb-3 mb-4 mt-5">Materials</h4>

      <b-table
        :show-empty="false"
        :items="filteredMaterials"
        :fields="fields"
        :busy="isLoading"
        head-variant="primary"
        borderless
        thead-tr-class="bg-light"
        tbody-tr-class="border-bottom"
      >
        <template v-slot:cell(material_id)="row">
          <template v-if="row.item.material"
            ><div class="d-flex flex-column">
              {{ row.item.material.code
              }}<small>{{ row.item.material.short_description }}</small>
            </div>
          </template>
        </template>
        <template v-slot:cell(quantity)="row">
          <b-form-input
            v-model.number="row.item.quantity"
            type="text"
            :style="{ maxWidth: '200px' }"
            class="text-center"
          ></b-form-input>
        </template>
        <template v-slot:cell(actions)="row">
          <b-btn variant="link" v-on:click="onRemove(row.item)"
            ><i class="bx bx-trash text-danger"></i
          ></b-btn>
        </template>
        <template v-slot:custom-foot>
          <b-btn variant="link" v-b-modal.modalProductSelector class="my-2">
            <i class="bx bx-plus-circle mr-1"></i>Add a Material
          </b-btn>
        </template>
      </b-table>

      <div class="d-flex justify-content-end border-top pt-3">
        <b-btn class="btn-label" variant="primary" v-on:click="onSave">
          <i class="bx bx-save label-icon"></i>Save Changes
        </b-btn>
      </div>
    </b-card>
    <app-product-selector-dialog
      id="modalProductSelector"
      :visible.sync="showProductSelector"
      v-on:input="onProductSelected"
      close-on-select
      :custom-filter="[{ is_material: { _eq: true } }]"
    />
  </g-overlay>
</template>
